// Live QR/Barcode scanner. Uses an isolated <CameraFeed /> so the video preview
// NEVER re-renders — eliminates the flicker. Auto-processes the decoded code.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QrCode, Images, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAccounts } from "@/lib/useAccounts";
import { parseUaeVatQr } from "@/lib/parseUaeVatQr";
import { identifyProduct } from "@/lib/identifyProduct";
import { CameraFeed } from "@/components/add/CameraFeed";
import { QrQuickAmount } from "@/components/add/QrQuickAmount";
import { beep, unlockAudio } from "@/lib/beep";
import { localToday } from "@/lib/localDate";

export function QrCapture({ onDone }) {
  const { lang } = useI18n();
  const { items: accounts = [] } = useAccounts();
  const AR = lang === "ar";
  const galRef = useRef(null);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState(null);
  const [quick, setQuick] = useState(null);
  const [busy, setBusy] = useState(false);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  useEffect(() => { unlockAudio(); }, []);

  const process = useCallback(async (text, frameDataUrl) => {
    beep();
    setBusy(true);
    const vat = parseUaeVatQr(text);
    const accs = accountsRef.current || [];
    if (vat && vat.total > 0 && accs.length > 0) {
      try {
        await api.post("/transactions", { type: "expense", amount: vat.total, currency: "AED",
          category: "groceries", description: vat.seller || "VAT invoice",
          date: localToday(), account_id: accs[0].id, force: true });
        toast.success(`${vat.seller || "VAT"} · ${Math.round(vat.total)} AED`);
        onDone?.(); return;
      } catch { toast.error(AR ? "تعذّر الحفظ" : "Save failed"); onDone?.(); return; }
    }
    if (/^\d{8,14}$/.test(text) || frameDataUrl) {
      const product = await identifyProduct(text, frameDataUrl);
      setQuick({ code: text, product }); setBusy(false); return;
    }
    toast.info(AR ? "الرمز لا يحتوي بيانات معاملة" : "Code has no purchase data"); onDone?.();
  }, [AR, onDone]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative w-full max-w-sm aspect-square rounded-3xl overflow-hidden neu-inset bg-black">
        {!quick && <CameraFeed onDetect={process} onReady={() => setStarting(false)} onError={(m) => { setStarting(false); setError(m); }} />}
        {(starting || busy) && <div className="absolute inset-0 grid place-items-center bg-black/60 text-white pointer-events-none"><Loader2 className="w-8 h-8 animate-spin" /></div>}
        <div className="pointer-events-none absolute inset-8 border-2 border-white/70 rounded-2xl" />
        {!quick && !busy && (
          <div className="pointer-events-none absolute left-8 right-8 top-8 bottom-8 overflow-hidden">
            <div className="absolute left-0 right-0 h-0.5 bg-red-500/90 qr-scan-line" />
          </div>
        )}
      </div>
      <p className="text-sm text-slate-600 text-center max-w-sm flex items-center gap-2">
        <QrCode className="w-4 h-4 text-primary" />
        {error ? (AR ? "تعذّر فتح الكاميرا" : "Camera unavailable") : (AR ? "وجّه الكاميرا نحو الرمز — سيُكتشف تلقائياً" : "Point the camera at a code")}
      </p>
      <input data-testid="qr-gal-input" ref={galRef} type="file" accept="image/*" className="hidden" />
      <QrQuickAmount open={!!quick} code={quick?.code} product={quick?.product}
        onSaved={() => { setQuick(null); onDone?.(); }} onClose={() => { setQuick(null); onDone?.(); }} />
    </div>
  );
}
